"use client";

import Image from "next/image";
import { motion } from "framer-motion";
import { Mountain, Waves, TreePine, Home } from "lucide-react";

const DESTINATIONS = [
  { icon: Mountain, label: "Mountain Hideaways" },
  { icon: Waves, label: "Coastal Escapes" },
  { icon: TreePine, label: "Plantation Retreats" },
  { icon: Home, label: "Countryside Villas" },
];

export function AboutVision() {
  return (
    <section className="section-padding relative overflow-hidden bg-background">
      <div className="mx-auto max-w-7xl">
        <div className="grid items-center gap-12 lg:grid-cols-5 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="relative aspect-[4/5] overflow-hidden rounded-2xl lg:col-span-2"
          >
            <Image
              src="/images/property.jpg"
              alt="Nest Villas — Pan-India luxury collection"
              fill
              className="object-cover"
              sizes="(max-width: 1024px) 100vw, 40vw"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
            <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />

            <div className="absolute bottom-6 left-6 right-6 rounded-xl border border-primary/30 bg-background/80 p-5 backdrop-blur-md">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary">
                Our Vision
              </p>
              <p className="mt-2 font-playfair text-2xl font-bold text-foreground md:text-3xl">
                100+ Properties
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                across India
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-3"
          >
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-primary">
              The Road Ahead
            </p>
            <h2 className="mt-3 font-playfair text-3xl font-bold leading-tight text-foreground md:text-4xl lg:text-5xl">
              A pan-India collection of luxury stays
            </h2>

            <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              <p>
                As we look ahead, Nest Villas is preparing for its next chapter
                with a vision to expand to{" "}
                <span className="font-semibold text-primary">
                  100+ properties across India
                </span>
                . Our goal is to build a pan-India collection of luxury villas
                and boutique stays that offer consistent quality, distinctive
                locations, and deeply personalised hospitality.
              </p>
              <p>
                From mountain hideaways to coastal escapes, plantation retreats
                to countryside villas, Nest Villas is building a collection of
                stays for those who want every holiday, celebration, retreat, or
                event to feel{" "}
                <span className="font-semibold text-foreground">
                  rare, refined, and truly memorable
                </span>
                .
              </p>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3">
              {DESTINATIONS.map((dest, i) => {
                const Icon = dest.icon;
                return (
                  <motion.div
                    key={dest.label}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.3 + i * 0.08 }}
                    className="flex items-center gap-3 rounded-lg border border-border/50 bg-card/40 px-4 py-3"
                  >
                    <Icon className="size-4 shrink-0 text-primary" />
                    <span className="text-sm font-medium text-foreground">
                      {dest.label}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
